/**
 * MatchedPatientsStep
 *
 * Fetches and displays matched patients split into two groups:
 *   1. Patients whose records will be enriched with new data
 *   2. Patients who were recognized but have no new data to add
 */

import { useMemo, useState } from 'react';
import {
  AutoAwesome as EnrichIcon,
  CheckCircleOutline as RecognizedIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGetReviewPhaseQuery } from '../../../store/hooks';
import type { MatchedPatientItem } from '../../../types';
import { TARGET_FIELD_LABELS } from '../../../types';
import type { MatchedPatientsStepProps } from '../ImportReviewWizard.types';

// =============================================================================
// Helpers
// =============================================================================

function formatFieldLabel(key: string): string {
  return (TARGET_FIELD_LABELS as Record<string, string>)[key]
    ?? key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function ConfidenceChip({ confidence }: { confidence: string }) {
  const colorMap: Record<string, 'success' | 'info' | 'warning'> = {
    exact: 'success',
    high: 'info',
    partial: 'warning',
    low: 'warning',
  };
  return (
    <Chip
      label={`${confidence} match`}
      color={colorMap[confidence] ?? 'default'}
      size="small"
      sx={{ height: 22, fontSize: '0.75rem' }}
    />
  );
}

function hasEnrichment(patient: MatchedPatientItem): boolean {
  return (patient.enrichment_fields ?? []).length > 0;
}

/** Safely convert a value to a displayable string (handles nested objects from the API). */
function displayValue(val: unknown): string {
  if (val == null) return '(empty)';
  if (typeof val === 'object') {
    // Flatten nested objects like { payer_name: "Aetna", policy_number: "123" }
    const entries = Object.entries(val as Record<string, unknown>)
      .filter(([, v]) => v != null && v !== '')
      .map(([, v]) => String(v));
    return entries.length > 0 ? entries.join(', ') : '(empty)';
  }
  return String(val) || '(empty)';
}

// =============================================================================
// Sub-components
// =============================================================================

/** Table for patients whose records will be enriched with new data */
function EnrichmentTable({ patients, rowOffset }: { patients: MatchedPatientItem[]; rowOffset: number }) {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: 'calc(100vh - 480px)', overflow: 'auto' }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 56 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Patient</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Match</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Fields Updated</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Changes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.map((patient, idx) => (
            <TableRow key={patient.extracted_patient_id} hover>
              <TableCell>{rowOffset + idx + 1}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {patient.matched_patient_name}
                </Typography>
              </TableCell>
              <TableCell>
                <ConfidenceChip confidence={patient.match_confidence} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {(patient.enrichment_fields ?? []).map(formatFieldLabel).join(', ') || '\u2014'}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {(patient.enrichment_fields ?? []).slice(0, 3).map((field) => {
                    const existingVal = displayValue(patient.existing[field]);
                    const incomingVal = displayValue(patient.incoming[field]);
                    return (
                      <Tooltip
                        key={field}
                        title={`${existingVal} → ${incomingVal}`}
                        arrow
                      >
                        <Typography variant="caption" noWrap sx={{ maxWidth: 260, display: 'block' }}>
                          <strong>{formatFieldLabel(field)}:</strong>{' '}
                          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                            {existingVal}
                          </span>
                          {' → '}
                          {incomingVal}
                        </Typography>
                      </Tooltip>
                    );
                  })}
                  {(patient.enrichment_fields ?? []).length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{(patient.enrichment_fields ?? []).length - 3} more
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Compact table for recognized patients with no data changes */
function RecognizedTable({ patients, rowOffset }: { patients: MatchedPatientItem[]; rowOffset: number }) {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: 300, overflow: 'auto' }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 56 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Patient</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Match</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.map((patient, idx) => (
            <TableRow key={patient.extracted_patient_id} hover>
              <TableCell>{rowOffset + idx + 1}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {patient.matched_patient_name}
                </Typography>
              </TableCell>
              <TableCell>
                <ConfidenceChip confidence={patient.match_confidence} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// =============================================================================
// Section Header
// =============================================================================

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  description: string;
}

function SectionHeader({ icon, title, count, description }: SectionHeaderProps) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
          <Typography component="span" variant="subtitle1" color="text.secondary" sx={{ ml: 0.5 }}>
            ({count})
          </Typography>
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
        {description}
      </Typography>
    </Box>
  );
}

// =============================================================================
// Component
// =============================================================================

const PAGE_SIZE = 100;

export const MatchedPatientsStep = ({ sessionId }: MatchedPatientsStepProps) => {
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching, error } = useGetReviewPhaseQuery({
    sessionId,
    phase: 'matched_patients',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const patients = (data?.items as MatchedPatientItem[] | undefined) ?? [];
  const totalCount = data?.total_count ?? patients.length;
  const rowOffset = page * PAGE_SIZE;

  const { enriched, recognized } = useMemo(() => {
    const enriched: MatchedPatientItem[] = [];
    const recognized: MatchedPatientItem[] = [];
    for (const p of patients) {
      if (hasEnrichment(p)) {
        enriched.push(p);
      } else {
        recognized.push(p);
      }
    }
    return { enriched, recognized };
  }, [patients]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load matched patients. Please try again.</Alert>
    );
  }

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Matched Patients
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We found {totalCount} patient{totalCount === 1 ? '' : 's'} from your file that already
          exist in your account. No duplicates will be created.
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {/* Group 1 — Enriched patients */}
        {enriched.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <SectionHeader
              icon={<EnrichIcon fontSize="small" color="primary" />}
              title="Updating Records"
              count={enriched.length}
              description="These patients have missing info that will be filled in from your file — e.g. email, phone, or insurance details."
            />
            <EnrichmentTable patients={enriched} rowOffset={rowOffset} />
          </Box>
        )}

        {/* Group 2 — Recognized, no data changes */}
        {recognized.length > 0 && (
          <Box>
            <SectionHeader
              icon={<RecognizedIcon fontSize="small" color="success" />}
              title="Already Up to Date"
              count={recognized.length}
              description="These patients were found in your file and are already in your account with complete records. No data changes needed — they may still receive new appointments."
            />
            <RecognizedTable patients={recognized} rowOffset={rowOffset + enriched.length} />
          </Box>
        )}

        {/* Empty state */}
        {patients.length === 0 && (
          <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No matched patients.
            </Typography>
          </Paper>
        )}
      </Box>

      {totalCount > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      )}
    </Box>
  );
};
